import { use } from 'react';
import { TeButton, TeContainer, TeContainerSlim, TeIcon, TeText } from '@topcon/tuicl-react';
import { PlaceholderContainer, useAuthentication } from '@topcon/topcon-utils';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import IconWrapper from '../../IconWrapper';
import { background, border } from '../../../providers/theme/theme.index';
import { ServiceAgreementTermsEnum } from '../../../api/generated/pm';
import {
  invalidateRequestBusinessPartner,
  useRequestBusinessPartner,
} from '../../../pages/RequestBusinessPartner/businessPartnerRequest.queries';
import { AppRoutes, useIsLogged } from '../../../app/routes/utils';
import { type ReviewResultContextType } from '../../../providers/ReviewResultContext/review-result.types';
import { ReviewResultContext } from '../../../providers/ReviewResultContext/review-result.context';
import AgreementAccessRight from '../../ServiceAgreement/AgreementAccessRight';
import ContinueCard from '../../common/ContinueCard/ContinueCard';
import CustomGrid from '../../common/CustomGrid/CustomGrid';
import TeContainerSlimWrapper from '../../common/PrimaryContainerSlim/PrimaryContainerSlim';
import useLoginRedirect from '../../../hooks/useLoginRedirect';
import { useAgreementResultStatusText } from '../../../hooks/useAgreementResultStatusText';

function ReviewRequestBusinessPartnerResultStep() {
  const { t } = useTranslation();
  const isLogged = useIsLogged();
  const [searchParam] = useSearchParams();
  const id = searchParam.get('resource');
  const navigate = useNavigate();
  const authenticationService = useAuthentication();
  const { result } = use<ReviewResultContextType>(ReviewResultContext);
  const { data: requestBusinessPartner, isLoading } = useRequestBusinessPartner(id);
  const requestBusinessPartnerTerms = requestBusinessPartner?.serviceAgreementTerms;
  const { redirectWithRegistrationToken } = useLoginRedirect();
  const text = useAgreementResultStatusText({
    result,
    dealerName: requestBusinessPartner?.provider?.name,
    customerName: requestBusinessPartner?.name,
    terms: requestBusinessPartnerTerms,
  });

  if (!id) {
    navigate(AppRoutes.Overview);
    return;
  }

  async function handleContinue() {
    await invalidateRequestBusinessPartner();
    if (result === 'accepted') {
      if (!authenticationService.identityId) {
        if (!requestBusinessPartner?.id) {
          return;
        }

        await redirectWithRegistrationToken(requestBusinessPartner.id);
        return;
      }
      navigate(AppRoutes.Notifications);
      return;
    }
    navigate(AppRoutes.Overview);
  }

  const summaryLabel = t('serviceAgreement.agreementReview.summary', {
    name: requestBusinessPartner?.name,
  });

  const AcceptedAgreementSection = () => {
    return (
      <TeContainerSlim
        fill
        grow
        column
        gap="l"
        borderColor={border.primary}
        borderRadius={4}
        padding="l"
        justifyContent="center"
        alignItems="center"
        width={576}
      >
        <TeText size={14} weight={400}>
          {summaryLabel}
        </TeText>
        <CustomGrid
          columns={requestBusinessPartnerTerms === ServiceAgreementTermsEnum.FULL_SERVICE ? 3 : 1}
          gap={0}
          padding={0}
        >
          {requestBusinessPartnerTerms === ServiceAgreementTermsEnum.FULL_SERVICE && (
            <AgreementAccessRight
              unsetWidth
              title={t('serviceAgreement.accessRights.activations.title')}
              description={t('serviceAgreement.accessRights.activations.description')}
            />
          )}
          {requestBusinessPartnerTerms === ServiceAgreementTermsEnum.FULL_SERVICE && (
            <AgreementAccessRight
              unsetWidth
              title={t('serviceAgreement.accessRights.administration.title')}
              description={t('serviceAgreement.accessRights.administration.description')}
            />
          )}
          <AgreementAccessRight
            unsetWidth
            title={t('serviceAgreement.accessRights.accessData.title')}
            description={t('serviceAgreement.accessRights.accessData.description')}
          />
        </CustomGrid>
      </TeContainerSlim>
    );
  };

  const DeclinedAgreementSection = () => (
    <TeText size={12} weight={400} color="text.secondary">
      {t('clickToContinue')}
    </TeText>
  );

  return (
    <TeContainer fill grow justifyContent="center" alignItems="center">
      {isLoading ? (
        <PlaceholderContainer grow hasLoader text={t('loading')} />
      ) : (
        <TeContainerSlimWrapper borderRadius="m">
          <TeContainerSlim fill grow padding="xl">
            <TeContainerSlim fill grow column justifyContent="center" alignItems="center" gap="xs">
              <IconWrapper backgroundColor={background.tertiary}>
                <TeIcon
                  color={
                    result === 'accepted' ? 'icon.success.primary' : 'background.error.quaternary'
                  }
                  name={result === 'accepted' ? 'check_circle' : 'cancel'}
                  outline
                  size="xl"
                />
              </IconWrapper>
              <TeContainer width={450}>
                <TeText weight={700} size={20} alignment="center">
                  {text}
                </TeText>
              </TeContainer>

              {result === 'accepted' ? <AcceptedAgreementSection /> : <DeclinedAgreementSection />}
              <TeContainerSlim
                padding={['m', 0]}
                gap="m"
                fill
                grow
                width={576}
                justifyContent="center"
              >
                {isLogged ? (
                  <TeButton text={t('continue')} size="s" teclick={handleContinue} />
                ) : (
                  <ContinueCard onContinue={handleContinue} />
                )}
              </TeContainerSlim>
            </TeContainerSlim>
          </TeContainerSlim>
        </TeContainerSlimWrapper>
      )}
    </TeContainer>
  );
}

export default ReviewRequestBusinessPartnerResultStep;
